<template>
  <div class="flex-1 overflow-auto p-6">
    <!-- QM §1.2 — see Index.vue: the route is reachable by URL regardless of the sidebar. -->
    <p v-if="!isQueueManagementEnabled" class="text-slate-600">
      {{ $t('QUEUES_MGMT.DISABLED') }}
    </p>
    <div v-else class="max-w-[75rem]">
      <h1 class="text-2xl font-medium text-slate-900 dark:text-slate-100 mb-1">
        {{ $t('QUEUES_MGMT.AUDIT.HEADER') }}
      </h1>
      <p class="text-sm text-slate-600 dark:text-slate-400 mb-4">
        {{ $t('QUEUES_MGMT.AUDIT.SUB_TEXT') }}
      </p>

      <queues-nav current="queues_audit" />

      <p v-if="!hasQueueSupervision" class="mt-6 text-slate-600">
        {{ $t('QUEUES_MGMT.SUPERVISOR.FORBIDDEN') }}
      </p>

      <template v-else>
        <!-- QM §5.9(1) — filters -->
        <div class="grid grid-cols-1 md:grid-cols-4 gap-3 mt-4">
          <label>
            {{ $t('QUEUES_MGMT.AUDIT.FILTERS.QUEUE') }}
            <select v-model="filters.queue_id" class="mb-0">
              <option :value="null">
                {{ $t('QUEUES_MGMT.AUDIT.FILTERS.ALL') }}
              </option>
              <option v-for="queue in queues" :key="queue.id" :value="queue.id">
                {{ queue.name }}
              </option>
            </select>
          </label>

          <label>
            {{ $t('QUEUES_MGMT.AUDIT.FILTERS.EVENT_TYPE') }}
            <select v-model="filters.event_type" class="mb-0">
              <option :value="null">
                {{ $t('QUEUES_MGMT.AUDIT.FILTERS.ALL') }}
              </option>
              <option v-for="type in eventTypes" :key="type" :value="type">
                {{ eventLabel(type) }}
              </option>
            </select>
          </label>

          <woot-input
            v-model.trim="filters.conversation_id"
            :label="$t('QUEUES_MGMT.AUDIT.FILTERS.TICKET')"
            :placeholder="$t('QUEUES_MGMT.AUDIT.FILTERS.TICKET_PLACEHOLDER')"
          />

          <label>
            {{ $t('QUEUES_MGMT.AUDIT.FILTERS.AGENT') }}
            <select v-model="filters.agent_id" class="mb-0">
              <option :value="null">
                {{ $t('QUEUES_MGMT.AUDIT.FILTERS.ALL') }}
              </option>
              <option v-for="agent in agents" :key="agent.id" :value="agent.id">
                {{ agent.name }}
              </option>
            </select>
          </label>

          <label>
            {{ $t('QUEUES_MGMT.AUDIT.FILTERS.SINCE') }}
            <input v-model="filters.since" type="date" class="mb-0" />
          </label>

          <label>
            {{ $t('QUEUES_MGMT.AUDIT.FILTERS.UNTIL') }}
            <input v-model="filters.until" type="date" class="mb-0" />
          </label>
        </div>

        <div class="flex items-center gap-2 mt-3 mb-4">
          <woot-button :is-loading="isLoading" @click="applyFilters">
            {{ $t('QUEUES_MGMT.AUDIT.FILTERS.APPLY') }}
          </woot-button>
          <woot-button
            variant="smooth"
            color-scheme="secondary"
            @click="resetFilters"
          >
            {{ $t('QUEUES_MGMT.AUDIT.FILTERS.RESET') }}
          </woot-button>
          <woot-button
            variant="smooth"
            icon="arrow-download"
            :is-loading="isExporting"
            @click="exportCsv"
          >
            {{ $t('QUEUES_MGMT.AUDIT.EXPORT') }}
          </woot-button>
        </div>

        <!-- QM §5.2, §5.9 — unobtrusive nudge rather than an auto-refresh -->
        <div
          v-if="hasNewEvents"
          class="flex items-center gap-2 mb-3 text-sm text-slate-600 dark:text-slate-400"
        >
          <span>{{ $t('QUEUES_MGMT.AUDIT.NEW_EVENTS') }}</span>
          <woot-button size="tiny" variant="smooth" @click="load">
            {{ $t('QUEUES_MGMT.CONNECTION.REFRESH') }}
          </woot-button>
        </div>

        <woot-loading-state
          v-if="isLoading"
          :message="$t('QUEUES_MGMT.AUDIT.LOADING')"
        />
        <p v-else-if="!events.length" class="text-slate-500">
          {{ $t('QUEUES_MGMT.AUDIT.EMPTY') }}
        </p>
        <table v-else class="woot-table">
          <thead>
            <th
              v-for="header in $t('QUEUES_MGMT.AUDIT.TABLE_HEADER')"
              :key="header"
            >
              {{ header }}
            </th>
          </thead>
          <tbody>
            <tr v-for="event in events" :key="event.id">
              <td>{{ formatTime(event.created_at) }}</td>
              <td>{{ eventLabel(event.event_type) }}</td>
              <td>
                <router-link
                  v-if="event.conversation_id"
                  :to="conversationUrl(event.conversation_id)"
                >
                  #{{ event.conversation_id }}
                </router-link>
                <span v-else>—</span>
              </td>
              <td>{{ queueName(event.queue_id) }}</td>
              <td>{{ actorName(event.actor_id) }}</td>
              <td>{{ agentName(event.subject_agent_id) }}</td>
              <td class="max-w-xs truncate" :title="payloadText(event.payload)">
                {{ payloadText(event.payload) }}
              </td>
            </tr>
          </tbody>
        </table>

        <!-- The API pages at a fixed size and returns no total, so step manually. -->
        <div v-if="!isLoading && events.length" class="flex gap-2 mt-3">
          <woot-button
            variant="smooth"
            size="small"
            :is-disabled="page === 1"
            @click="changePage(page - 1)"
          >
            {{ $t('QUEUES_MGMT.AUDIT.PREV') }}
          </woot-button>
          <span class="text-sm text-slate-600 self-center">
            {{ $t('QUEUES_MGMT.AUDIT.PAGE', { page }) }}
          </span>
          <woot-button
            variant="smooth"
            size="small"
            :is-disabled="events.length < PER_PAGE"
            @click="changePage(page + 1)"
          >
            {{ $t('QUEUES_MGMT.AUDIT.NEXT') }}
          </woot-button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { endOfDay, format, parseISO, startOfDay } from 'date-fns';
import alertMixin from 'shared/mixins/alertMixin';
import queueSupervisionMixin from 'dashboard/mixins/queueSupervision';
import QueueAuditEventsAPI from 'dashboard/api/queueAuditEvents';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import QueuesNav from './QueuesNav.vue';

// Mirrors QueueAuditEventsController::RESULTS_PER_PAGE.
const PER_PAGE = 50;

// QM §5.9(3) — the export is a background job, so the screen polls for its result.
const EXPORT_POLL_INTERVAL = 2000;
const EXPORT_POLL_TIMEOUT = 5 * 60 * 1000;

const EVENT_TYPES = [
  'automatic_assignment',
  'manual_assignment',
  'manual_pull',
  'queue_changed',
  'returned_to_queue',
  'auto_returned',
  'overflow_expansion_fired',
  'lock_applied',
  'lock_released',
  'left_unassigned',
];

const emptyFilters = () => ({
  queue_id: null,
  event_type: null,
  conversation_id: '',
  agent_id: null,
  since: '',
  until: '',
});

export default {
  components: {
    QueuesNav,
  },
  mixins: [alertMixin, queueSupervisionMixin],
  data() {
    return {
      PER_PAGE,
      eventTypes: EVENT_TYPES,
      filters: emptyFilters(),
      events: [],
      page: 1,
      isLoading: false,
      isExporting: false,
      exportPollTimer: null,
      exportDeadline: 0,
      hasNewEvents: false,
    };
  },
  computed: {
    ...mapGetters({
      queues: 'queues/getQueues',
      agents: 'agents/getVerifiedAgents',
    }),
    // Only non-empty filters are sent so the backend scopes stay no-ops.
    activeFilters() {
      const { since, until, ...rest } = this.filters;
      return Object.fromEntries(
        Object.entries({
          ...rest,
          // QM §5.9(1) — the date inputs give a bare day.
          since: since && startOfDay(parseISO(since)).toISOString(),
          until: until && endOfDay(parseISO(until)).toISOString(),
        }).filter(([, value]) => value !== null && value !== '')
      );
    },
  },
  mounted() {
    if (!this.isQueueManagementEnabled || !this.hasQueueSupervision) {
      return;
    }
    this.$store.dispatch('queues/get');
    this.$store.dispatch('agents/get');
    this.load();
    // QM §5.2 — this screen has no live-tailing requirement of its own
    window.bus.$on(BUS_EVENTS.CONVERSATION_UPDATED, this.onConversationUpdated);
  },
  beforeDestroy() {
    window.bus.$off(
      BUS_EVENTS.CONVERSATION_UPDATED,
      this.onConversationUpdated
    );
    this.stopExportPolling();
  },
  methods: {
    onConversationUpdated() {
      this.hasNewEvents = true;
    },
    conversationUrl(conversationId) {
      return frontendURL(
        `accounts/${this.currentAccountId}/conversations/${conversationId}`
      );
    },
    formatTime(value) {
      return value ? format(parseISO(value), 'dd/MM/yy HH:mm') : '—';
    },
    eventLabel(type) {
      return this.$t(`QUEUES_MGMT.AUDIT.EVENT_TYPES.${type.toUpperCase()}`);
    },
    queueName(queueId) {
      const queue = this.queues.find(item => item.id === queueId);
      return queue ? queue.name : '—';
    },
    agentName(agentId) {
      const agent = this.agents.find(item => item.id === agentId);
      return agent ? agent.name : '—';
    },
    // A null actor is the engine acting on its own (auto-assign, auto-return).
    actorName(actorId) {
      if (!actorId) {
        return this.$t('QUEUES_MGMT.AUDIT.SYSTEM_ACTOR');
      }
      return this.agentName(actorId);
    },
    payloadText(payload) {
      if (!payload || !Object.keys(payload).length) {
        return '—';
      }
      return JSON.stringify(payload);
    },
    async load() {
      this.isLoading = true;
      try {
        const { data } = await QueueAuditEventsAPI.list({
          ...this.activeFilters,
          page: this.page,
        });
        this.events = data.payload;
        this.hasNewEvents = false;
      } catch (error) {
        this.showAlert(this.$t('QUEUES_MGMT.AUDIT.TOAST.ERROR'));
      } finally {
        this.isLoading = false;
      }
    },
    applyFilters() {
      this.page = 1;
      this.load();
    },
    resetFilters() {
      this.filters = emptyFilters();
      this.applyFilters();
    },
    changePage(page) {
      this.page = page;
      this.load();
    },
    // QM §5.9(3) — enqueue the export, then poll until the file is ready.
    async exportCsv() {
      if (this.isExporting) {
        return;
      }
      this.isExporting = true;
      try {
        await QueueAuditEventsAPI.startExport(this.activeFilters);
        this.showAlert(this.$t('QUEUES_MGMT.AUDIT.TOAST.EXPORT_STARTED'));
        this.exportDeadline = Date.now() + EXPORT_POLL_TIMEOUT;
        this.pollExport();
      } catch (error) {
        this.failExport();
      }
    },
    pollExport() {
      this.exportPollTimer = setTimeout(async () => {
        try {
          const { data } = await QueueAuditEventsAPI.exportStatus();
          if (data.status === 'completed' && data.download_url) {
            this.finishExport(data.download_url);
          } else if (data.status === 'failed') {
            this.failExport();
          } else if (Date.now() > this.exportDeadline) {
            this.failExport();
          } else {
            this.pollExport();
          }
        } catch (error) {
          this.failExport();
        }
      }, EXPORT_POLL_INTERVAL);
    },
    finishExport(url) {
      this.stopExportPolling();
      this.isExporting = false;
      window.location.assign(url);
    },
    failExport() {
      this.stopExportPolling();
      this.isExporting = false;
      this.showAlert(this.$t('QUEUES_MGMT.AUDIT.TOAST.EXPORT_ERROR'));
    },
    stopExportPolling() {
      if (this.exportPollTimer) {
        clearTimeout(this.exportPollTimer);
        this.exportPollTimer = null;
      }
    },
  },
};
</script>
